import { useEffect, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { API_BASE } from '../../config'

export type FaqItem = {
  id?: number
  question: string
  answer: string
}

function stripHtml(html: string) {
  return String(html || '')
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
}

function looksLikeHtml(s: string) {
  return /<\/?[a-z][\s\S]*>/i.test(s)
}

function FaqAccordionItem({
  faq,
  index,
  isOpen,
  onToggle,
}: {
  faq: FaqItem
  index: number
  isOpen: boolean
  onToggle: () => void
}) {
  const html = looksLikeHtml(faq.answer)
  return (
    <motion.div
      className="card-glass overflow-hidden"
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: Math.min(index * 0.05, 0.35) }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 p-4 sm:p-5 md:p-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:focus-visible:ring-accent/40"
        aria-expanded={isOpen}
      >
        <span className="font-semibold text-slate-800 dark:text-slate-100 text-sm sm:text-base pr-2">
          {faq.question}
        </span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="shrink-0 text-primary dark:text-accent"
        >
          <ChevronDown size={20} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 sm:px-5 md:px-6 pb-4 sm:pb-5 md:pb-6 pt-0 text-slate-600 dark:text-slate-400 text-sm sm:text-base leading-relaxed border-t border-slate-200/50 dark:border-slate-700/50 mt-0.5">
              {html ? (
                <div
                  className="prose prose-sm sm:prose-base dark:prose-invert max-w-none pt-3"
                  dangerouslySetInnerHTML={{ __html: faq.answer }}
                />
              ) : (
                <p className="pt-3 whitespace-pre-line">{faq.answer}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

type Props = {
  /** CMS location key: global | clients | digital-marketing-agency-surat | … */
  location: string
  title?: string
  subtitle?: string
  /** Used when API is empty (e.g. city page static fallback) */
  fallback?: FaqItem[]
  className?: string
  /** Inject FAQPage JSON-LD for AEO */
  schema?: boolean
  schemaId?: string
}

export function FaqSection({
  location,
  title = 'Frequently Asked Questions',
  subtitle = 'Straight answers to common questions.',
  fallback = [],
  className = '',
  schema = true,
  schemaId = 'faq-section-ld',
}: Props) {
  const [faqs, setFaqs] = useState<FaqItem[]>(() => fallback)
  const [openIndex, setOpenIndex] = useState<number | null>(0)
  const [loaded, setLoaded] = useState(false)
  const fallbackRef = useRef(fallback)
  fallbackRef.current = fallback

  useEffect(() => {
    let cancelled = false
    setLoaded(false)
    fetch(`${API_BASE}/api/public/faqs?location=${encodeURIComponent(location)}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return
        if (data?.success && Array.isArray(data.faqs) && data.faqs.length > 0) {
          setFaqs(
            data.faqs.map((f: { id?: number; question: string; answer: string }) => ({
              id: f.id,
              question: f.question,
              answer: f.answer,
            }))
          )
        } else if (fallbackRef.current.length) {
          setFaqs(fallbackRef.current)
        } else {
          setFaqs([])
        }
      })
      .catch(() => {
        if (!cancelled && fallbackRef.current.length) setFaqs(fallbackRef.current)
      })
      .finally(() => {
        if (!cancelled) setLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [location])

  const faqSchema = useMemo(() => {
    if (!schema || faqs.length === 0) return null
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: stripHtml(f.answer),
        },
      })),
    }
  }, [faqs, schema])

  if (loaded && faqs.length === 0) return null

  return (
    <section className={className} aria-labelledby={`${schemaId}-heading`}>
      {faqSchema && (
        <script
          id={schemaId}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
      <div className="max-w-3xl mx-auto">
        <motion.div
          className="text-center mb-8 sm:mb-10"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2
            id={`${schemaId}-heading`}
            className="text-2xl sm:text-3xl font-bold text-primary dark:text-white mb-3"
          >
            {title}
          </h2>
          {subtitle && (
            <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </motion.div>

        {!loaded && faqs.length === 0 ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 rounded-2xl bg-slate-200/50 dark:bg-slate-700/30 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {faqs.map((faq, i) => (
              <FaqAccordionItem
                key={faq.id ?? `${faq.question}-${i}`}
                faq={faq}
                index={i}
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex((prev) => (prev === i ? null : i))}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
